/**
 * Derived records — spec 5, tier 3. All free from the match log; nothing here is
 * stored.
 */

import { PLACEMENT_GAMES, isRanked, rankFor, rankThrough } from './elo.js';
import type { Rank } from './elo.js';
import {
  LETHAL_COMMANDER_DAMAGE,
  LETHAL_POISON,
  STARTING_LIFE,
  pilotOfCommander,
} from './live.js';
import { isRated } from './types.js';
import type { Deck, Match } from './types.js';

/**
 * How many decks name this member as their owner.
 *
 * Ownership is a label, not a claim — anyone can play anything, which is the
 * whole point. It is worth showing because it says who has been building, which
 * nothing else on the screen does.
 */
export function decksOwnedBy(decks: readonly Deck[], pilotId: string): number {
  return decks.filter((d) => d.ownerId === pilotId && !d.retired).length;
}

export interface Record2 {
  wins: number;
  losses: number;
}

export const emptyRecord = (): Record2 => ({ wins: 0, losses: 0 });

export const games = (r: Record2): number => r.wins + r.losses;

export function winRate(r: Record2): number | null {
  const n = games(r);
  return n === 0 ? null : r.wins / n;
}

export function formatRecord(r: Record2): string {
  return `${r.wins}–${r.losses}`;
}

/**
 * Where somebody finished, as a score from 0 to 1.
 *
 * Raw placings do not compare across pod sizes — second of two is last and
 * second of four is a good night — so a place is normalised by how many people
 * it was against: 1 for a win, 0 for last, and the halves in between. This is
 * exactly what the rating engine scores a player against, so the two can never
 * disagree about what a result was worth.
 *
 * Null when the game recorded no order, which is every game logged after the
 * fact. Averaging those in as wins-or-nothing would quietly mix two different
 * measurements.
 */
export function finishScore(match: Match, pilotId: string): number | null {
  const n = match.entries.length;
  if (n < 2) return null;
  if (!match.standing) return null;
  const mine = match.standing[pilotId];
  if (mine === undefined) return null;

  // Ties share the credit for the places they consumed.
  const better = match.entries.filter((e) => (match.standing![e.pilotId] ?? 0) < mine).length;
  const tied = match.entries.filter((e) => match.standing![e.pilotId] === mine).length;
  const beaten = n - better - tied;
  return (beaten + (tied - 1) / 2) / (n - 1);
}

export interface FinishRecord {
  /** Games that recorded a finishing order. */
  games: number;
  /** Mean raw placing — 1st, 2nd — which only means something with the pod size. */
  averagePlace: number;
  /** Mean pod size those placings were against. */
  averagePod: number;
  /** Mean normalised finish, 0 to 1. Half is exactly average for any pod size. */
  score: number;
}

export function finishRecord(
  matches: readonly Match[],
  pilotId: string,
): FinishRecord | null {
  const played = matches.filter(
    (m) =>
      isRated(m) &&
      m.standing?.[pilotId] !== undefined &&
      m.entries.some((e) => e.pilotId === pilotId),
  );
  if (played.length === 0) return null;

  let place = 0;
  let pod = 0;
  let score = 0;
  for (const m of played) {
    place += m.standing![pilotId]!;
    pod += m.entries.length;
    score += finishScore(m, pilotId) ?? 0;
  }
  return {
    games: played.length,
    averagePlace: place / played.length,
    averagePod: pod / played.length,
    score: score / played.length,
  };
}

/** How one person has done on one deck. */
export interface DeckForm {
  deckId: string;
  games: number;
  wins: number;
  /**
   * Share of opponents finished above, averaged — null where none of the games
   * recorded a finishing order, which is every game logged by hand.
   */
  finish: FinishRecord | null;
}

/**
 * How well somebody does on each deck they have played.
 *
 * Keyed on the pairing rather than on either half: a deck's own rating answers
 * "is this deck strong" and a guildmate's answers "are they good", and neither
 * answers "does this deck suit them", which is the only question a table
 * actually argues about.
 */
export function deckFormFor(matches: readonly Match[], pilotId: string): DeckForm[] {
  const byDeck = new Map<string, Match[]>();
  for (const m of matches) {
    if (!isRated(m)) continue;
    const entry = m.entries.find((e) => e.pilotId === pilotId);
    if (!entry) continue;
    const list = byDeck.get(entry.deckId);
    if (list) list.push(m);
    else byDeck.set(entry.deckId, [m]);
  }

  return [...byDeck.entries()].map(([deckId, played]) => ({
    deckId,
    games: played.length,
    wins: played.filter((m) => m.winnerPilotId === pilotId).length,
    finish: finishRecord(played, pilotId),
  }));
}

/**
 * How many games on one deck before it is worth calling anybody good with it.
 *
 * Three, which is not many and is honest about it. Commander is swingy enough
 * that one game says nothing at all and two say very little; the screen quotes
 * the count beside the claim so the reader can discount it themselves. Setting
 * this where a statistician would want it — twenty, thirty — would mean a
 * guild this size never sees the line at all, which is not more honest, only
 * emptier.
 */
export const FORM_MIN_GAMES = 3;

/**
 * The deck somebody is best on, or null if nothing has been played enough.
 *
 * Ranked on the share of opponents beaten where the games recorded a finishing
 * order, because that is what the profile already means by doing well: only
 * one seat wins a four-player pod, so a win rate makes a good season look like
 * a bad one. Falls back to wins per game for anybody whose games were all typed
 * in by hand, where no order was ever recorded.
 */
export function bestDeckFor(
  matches: readonly Match[],
  pilotId: string,
  minGames = FORM_MIN_GAMES,
): DeckForm | null {
  const eligible = deckFormFor(matches, pilotId).filter((d) => d.games >= minGames);
  if (eligible.length === 0) return null;

  const rank = (d: DeckForm) => (d.finish ? d.finish.score : d.wins / d.games);
  // Ties go to the deck with more games behind it: same claim, better evidence.
  return eligible.reduce((best, d) =>
    rank(d) > rank(best) || (rank(d) === rank(best) && d.games > best.games) ? d : best,
  );
}

/** One step of a rating's life: where it landed, and what put it there. */
export interface EloPoint {
  /** The game that moved it. Null for the point before the first one. */
  matchId: string | null;
  /** When it happened. The opening point borrows the first game's date. */
  ts: number;
  elo: number;
  /** What that game was worth. Zero at the opening point. */
  delta: number;
  /**
   * True while this was still a placement game.
   *
   * Those swing at double K and are not a rating yet, so a chart that draws
   * them the same as the rest is claiming a steadiness the number did not have.
   */
  placement: boolean;
  won: boolean;
}

/**
 * A rating's whole history, oldest first, starting from where it was before any
 * of these games.
 *
 * Rewound out of the stored deltas rather than kept as a column. The deltas are
 * exact — they are what was applied — so replaying them cannot disagree with
 * the rating they produced, which a separately maintained history eventually
 * would.
 */
function eloSeries(
  matches: readonly Match[],
  currentElo: number,
  played: (m: Match) => boolean,
  deltaOf: (m: Match) => number,
  wonBy: (m: Match) => boolean,
): EloPoint[] {
  const mine = matches.filter((m) => isRated(m) && played(m)).sort((a, b) => a.ts - b.ts);

  let elo = currentElo;
  for (const m of mine) elo -= deltaOf(m);

  const series: EloPoint[] = [
    {
      matchId: null,
      ts: mine[0]?.ts ?? Date.now(),
      elo,
      delta: 0,
      // Before a first game there is no rating to be placed, so the run of
      // provisional points starts here.
      placement: true,
      won: false,
    },
  ];
  mine.forEach((m, i) => {
    const delta = deltaOf(m);
    elo += delta;
    series.push({
      matchId: m.id,
      ts: m.ts,
      elo,
      delta,
      // The first five games are the placement games, here as everywhere else.
      placement: i < PLACEMENT_GAMES,
      won: wonBy(m),
    });
  });
  return series;
}

export function pilotEloSeries(
  matches: readonly Match[],
  pilotId: string,
  currentElo: number,
): EloPoint[] {
  return eloSeries(
    matches,
    currentElo,
    (m) => m.entries.some((e) => e.pilotId === pilotId),
    (m) => m.deltas.pilots[pilotId] ?? 0,
    (m) => m.winnerPilotId === pilotId,
  );
}

export function deckEloSeries(
  matches: readonly Match[],
  deckId: string,
  currentElo: number,
): EloPoint[] {
  return eloSeries(
    matches,
    currentElo,
    (m) => m.entries.some((e) => e.deckId === deckId),
    (m) => m.deltas.decks[deckId] ?? 0,
    (m) => m.entries.find((e) => e.deckId === deckId)?.pilotId === m.winnerPilotId,
  );
}

/*
 * `pilotEloHistory` used to live here — the series projected down to just its
 * ratings, because that was all the rank ladder walked. The ladder now needs
 * the deltas too, to know whether a game in grace lost ground, and `EloPoint`
 * already carries both. A ratings-only projection sitting next to it would be
 * a thing that still compiles and quietly answers the old question, so it is
 * gone: callers pass `pilotEloSeries` straight to `rankThrough`.
 */

/** A rank somebody was actually wearing at a point in time. */
export interface StandingAt {
  rank: Rank;
  /** Held on grace: below the floor, not yet given up. */
  held: boolean;
  /** False while they were still in placement, when there was no rank yet. */
  ranked: boolean;
  /** What the rating stood at once the game had been scored. */
  elo: number;
}

/**
 * The rank somebody was wearing when a particular game finished.
 *
 * A game in the history is a thing that happened on a night, and the halo on it
 * should say who they were that night. Drawing today's rank on it rewrites the
 * past every time somebody plays: a run of games somebody won on their way up
 * all end up wearing the rank they arrived at, and the climb — the only thing
 * a history is for — becomes invisible.
 *
 * Walks the same replayed deltas the live ladder walks, truncated at this game,
 * so a rank here and a rank on the standings can never be derived differently.
 * `rankThrough` needs the path and not the point, because whether a rank has
 * been given up is a question about how it was arrived at.
 *
 * Null if this member was not in that game, or if the game is not in the log.
 */
export function pilotRankAt(
  matches: readonly Match[],
  pilotId: string,
  currentElo: number,
  matchId: string,
): StandingAt | null {
  const series = pilotEloSeries(matches, pilotId, currentElo);
  const at = series.findIndex((p) => p.matchId === matchId);
  if (at < 0) return null;

  const history = series.slice(0, at + 1);
  const standing = rankThrough(history);
  const elo = history[history.length - 1]!.elo;
  return {
    rank: rankFor(elo, standing.index),
    held: standing.held,
    // The first entry is the rating before any game was played, so this game
    // was their `at`-th — and having played it is what counts towards placement.
    ranked: isRanked(at),
    elo,
  };
}

export interface CalibrationBucket {
  /** Lower bound of the bucket, 0 to 1. */
  from: number;
  to: number;
  /** How many seat-predictions fell in it. */
  n: number;
  /** What the model said, averaged. */
  predicted: number;
  /** What actually happened. */
  actual: number;
}

export interface Calibration {
  buckets: CalibrationBucket[];
  /** Every seat-prediction the league has made. */
  n: number;
  /**
   * Brier score: the mean squared error of the forecasts.
   *
   * Zero is perfect. It is the honest single number for a forecast because,
   * unlike accuracy, it punishes a confident wrong answer more than a hedged
   * one — which is the entire difference between a model and a guess.
   */
  brier: number;
  /**
   * What the same games score by knowing nothing at all.
   *
   * Not 0.25. That is the figure for calling a two-player game a coin flip, and
   * a four-player table split four ways scores 0.1875 while still knowing
   * nothing — so measuring against 0.25 would let an uninformed model look
   * clever purely by playing four-handed. This gives every seat one over the
   * pod size, on the same games, and is the only fair thing to beat.
   */
  baseline: number;
  /**
   * How much better than knowing nothing, as a fraction. Zero is no better,
   * one is perfect, and below zero means the model is worse than silence.
   */
  skill: number;
}

/**
 * Whether the odds the table was shown turn out to be true.
 *
 * The prediction is frozen at kick-off precisely so this can be asked later.
 * Seats given 70% should win about 70% of the time; if they win 45%, the model
 * is confident and wrong, and the number on the setup screen is decoration.
 */
export function calibration(matches: readonly Match[], bucketCount = 5): Calibration {
  const points: { p: number; naive: number; won: boolean }[] = [];
  for (const m of matches) {
    // A forecast for a game that never resolved is not a forecast that failed.
    if (!isRated(m) || !m.predicted) continue;
    for (const entry of m.entries) {
      const p = m.predicted[entry.pilotId];
      if (typeof p !== 'number' || !Number.isFinite(p)) continue;
      points.push({
        p,
        naive: 1 / m.entries.length,
        won: entry.pilotId === m.winnerPilotId,
      });
    }
  }

  const width = 1 / bucketCount;
  const buckets: CalibrationBucket[] = [];
  for (let i = 0; i < bucketCount; i++) {
    const from = i * width;
    const to = (i + 1) * width;
    // The top bucket owns its upper edge; every other one is half-open.
    const inside = points.filter((pt) => pt.p >= from && (i === bucketCount - 1 ? pt.p <= to : pt.p < to));
    buckets.push({
      from,
      to,
      n: inside.length,
      predicted: inside.length ? inside.reduce((s, pt) => s + pt.p, 0) / inside.length : 0,
      actual: inside.length ? inside.filter((pt) => pt.won).length / inside.length : 0,
    });
  }

  const score = (pick: (pt: (typeof points)[number]) => number) =>
    points.length
      ? points.reduce((s, pt) => s + Math.pow(pick(pt) - (pt.won ? 1 : 0), 2), 0) / points.length
      : 0;

  const brier = score((pt) => pt.p);
  const baseline = score((pt) => pt.naive);

  return {
    buckets,
    n: points.length,
    brier,
    baseline,
    skill: baseline > 0 ? 1 - brier / baseline : 0,
  };
}

/** Matches involving a deck, newest first. */
export function matchesForDeck(matches: readonly Match[], deckId: string): Match[] {
  return matches
    .filter((m) => isRated(m) && m.entries.some((e) => e.deckId === deckId))
    .sort((a, b) => b.ts - a.ts);
}

export function matchesForPilot(matches: readonly Match[], pilotId: string): Match[] {
  return matches
    .filter((m) => isRated(m) && m.entries.some((e) => e.pilotId === pilotId))
    .sort((a, b) => b.ts - a.ts);
}

export function deckRecord(matches: readonly Match[], deckId: string): Record2 {
  const r = emptyRecord();
  for (const m of matches) {
    if (!isRated(m)) continue;
    const entry = m.entries.find((e) => e.deckId === deckId);
    if (!entry) continue;
    if (entry.pilotId === m.winnerPilotId) r.wins++;
    else r.losses++;
  }
  return r;
}

export function pilotRecord(matches: readonly Match[], pilotId: string): Record2 {
  const r = emptyRecord();
  for (const m of matches) {
    if (!isRated(m)) continue;
    if (!m.entries.some((e) => e.pilotId === pilotId)) continue;
    if (m.winnerPilotId === pilotId) r.wins++;
    else r.losses++;
  }
  return r;
}

/**
 * Spec 6.2 — "4-1 in your hands, 1-3 in his" is the single most interesting
 * sentence the app can produce.
 */
export function deckRecordByPilot(
  matches: readonly Match[],
  deckId: string,
): Record<string, Record2> {
  const out: Record<string, Record2> = {};
  for (const m of matches) {
    if (!isRated(m)) continue;
    const entry = m.entries.find((e) => e.deckId === deckId);
    if (!entry) continue;
    const rec = (out[entry.pilotId] ??= emptyRecord());
    if (entry.pilotId === m.winnerPilotId) rec.wins++;
    else rec.losses++;
  }
  return out;
}

/** Head-to-head between two decks, over matches where both were present. */
export function headToHead(
  matches: readonly Match[],
  deckA: string,
  deckB: string,
): { record: Record2; matches: Match[] } {
  const shared = matches
    .filter(
      (m) =>
        isRated(m) &&
        m.entries.some((e) => e.deckId === deckA) &&
        m.entries.some((e) => e.deckId === deckB),
    )
    .sort((a, b) => b.ts - a.ts);

  const record = emptyRecord();
  for (const m of shared) {
    const a = m.entries.find((e) => e.deckId === deckA)!;
    const b = m.entries.find((e) => e.deckId === deckB)!;
    // Only counts when one of the two actually took the game; in a four-player
    // pod a third deck can win and neither of these beat the other.
    if (a.pilotId === m.winnerPilotId) record.wins++;
    else if (b.pilotId === m.winnerPilotId) record.losses++;
  }
  return { record, matches: shared };
}

/**
 * Spec 5, tier 3 — surfaces neglected decks without an obligation mechanic.
 * Returns null for a deck that has never been played.
 */
export function gamesSinceLastPlayed(matches: readonly Match[], deckId: string): number | null {
  const ordered = matches.filter(isRated).sort((a, b) => b.ts - a.ts);
  const idx = ordered.findIndex((m) => m.entries.some((e) => e.deckId === deckId));
  return idx === -1 ? null : idx;
}

/**
 * The night a game belongs to, as the moment that night began.
 *
 * Calendar day, which is the same thing History groups by. Not perfect — a game
 * that finishes at one in the morning was part of the evening before — but it
 * needs nothing recorded that is not already there, and the alternative is
 * asking people to declare when a session started.
 */
export function nightStart(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Every night the guild played, newest first. */
export function leagueNights(matches: readonly Match[]): number[] {
  const nights = new Set(matches.filter(isRated).map((m) => nightStart(m.ts)));
  return [...nights].sort((a, b) => b - a);
}

/** How many nights of play have happened since a moment. */
function nightsSince(nights: readonly number[], ts: number): number {
  // Strictly after, so the night something happened on does not count itself:
  // a deck played on the most recent night is zero nights idle, not one.
  return nights.filter((n) => n > ts).length;
}

/**
 * Nights of play since this deck last hit the table.
 *
 * Nights rather than games, because games is a measure of how busy the guild
 * has been and nights is a measure of how long it has been. A deck skipped
 * through a fourteen-game weekend has been skipped once.
 *
 * Null for a deck that has never been played at all.
 */
export function nightsSinceLastPlayed(matches: readonly Match[], deckId: string): number | null {
  const last = matches
    .filter((m) => isRated(m) && m.entries.some((e) => e.deckId === deckId))
    .reduce((latest, m) => Math.max(latest, m.ts), -Infinity);
  if (!Number.isFinite(last)) return null;
  return nightsSince(leagueNights(matches), last);
}

/**
 * How many nights a deck can sit out before the app mentions it.
 *
 * Three, for a group that plays about weekly — roughly a month of a deck
 * staying in the box. Short enough to be actionable, long enough that missing
 * one evening is not an accusation.
 */
export const NEGLECT_NIGHTS = 3;

export interface Neglected {
  deck: Deck;
  /** Nights of play it has sat out. */
  nights: number;
  /** When it last played, or null if it never has. */
  lastPlayed: number | null;
}

/**
 * Decks that have not seen the table in a while, longest-neglected first.
 *
 * A deck that has never been played is measured from the night it was
 * registered rather than excluded. Registered tonight is not neglect; still
 * sleeved after a month of guild nights is exactly what this is for.
 *
 * Empty until the guild has played enough nights for the number to mean
 * anything — otherwise a league one evening old reports its entire pool as
 * neglected, which is both true and useless.
 */
export function decksNeedingAGame(
  matches: readonly Match[],
  decks: readonly Deck[],
  after: number = NEGLECT_NIGHTS,
): Neglected[] {
  const nights = leagueNights(matches);
  if (nights.length <= after) return [];

  const out: Neglected[] = [];
  for (const deck of decks) {
    if (deck.retired) continue;
    const played = matches.filter((m) => isRated(m) && m.entries.some((e) => e.deckId === deck.id));
    const lastPlayed = played.length
      ? played.reduce((latest, m) => Math.max(latest, m.ts), -Infinity)
      : null;
    const idle = nightsSince(nights, lastPlayed ?? deck.createdAt);
    if (idle > after) out.push({ deck, nights: idle, lastPlayed });
  }

  return out.sort(
    (a, b) => b.nights - a.nights || (a.lastPlayed ?? 0) - (b.lastPlayed ?? 0),
  );
}

/**
 * Where somebody came, and whether that was last.
 *
 * Last is not the same as "did not win". Second of four is a good night, and
 * the rating agrees — second in an even field gains points. Anything reading
 * these two as one thing is going to say something unkind and untrue.
 *
 * Ties count as last only when nobody finished below them: three survivors tied
 * second in a four-player pod are all second, because none of them was
 * eliminated and the game gave no way to separate them.
 */
export function placingOf(
  match: Match,
  pilotId: string,
): { place: number | undefined; last: boolean; won: boolean } {
  const won = match.winnerPilotId === pilotId;
  const place = match.standing?.[pilotId];
  if (won) return { place, last: false, won };
  // Without a recorded order there are only two answers, which is exactly what
  // a game typed in after the fact knows. Everyone who did not win is level.
  if (place === undefined) return { place: undefined, last: true, won };
  return { place, last: place >= match.entries.length, won };
}

export type ExitKind = 'won' | 'survived' | 'poison' | 'commander' | 'life' | 'unknown';

export interface Exit {
  kind: ExitKind;
  /** The turn they went out on, when the log is detailed enough to say. */
  turn?: number;
  /** Whose commander did it. */
  source?: string;
  /** What they had left, for somebody still standing. */
  life?: number;
}

/**
 * The last thing that happened to a player.
 *
 * Checked in the order the rules care about: poison and commander damage kill
 * whatever the life total says, so a player on nine life with ten poison
 * counters was poisoned out and not burned down.
 */
export function exitOf(match: Match, pilotId: string): Exit {
  if (match.winnerPilotId === pilotId) return { kind: 'won' };

  if ((match.poison?.[pilotId] ?? 0) >= LETHAL_POISON) return { kind: 'poison' };

  const commander = Object.entries(match.commanderDamage?.[pilotId] ?? {}).find(
    ([, dealt]) => dealt >= LETHAL_COMMANDER_DAMAGE,
  );
  // The key names a commander; the sheet wants to name the person holding it.
  if (commander) return { kind: 'commander', source: pilotOfCommander(commander[0]) };

  const life = match.lifeRemaining?.[pilotId];
  if (life !== undefined && life > 0) return { kind: 'survived', life };

  const turn = turnTheyFell(match, pilotId);
  if (turn !== undefined) return { kind: 'life', turn };
  return life !== undefined ? { kind: 'life' } : { kind: 'unknown' };
}

/** The turn a life total first crossed zero, replayed from the damage log. */
function turnTheyFell(match: Match, pilotId: string): number | undefined {
  const log = match.damageLog;
  const endTurn = match.endTurn ?? 0;
  if (!log?.length || endTurn < 1) return undefined;

  let life = STARTING_LIFE;
  for (let turn = 1; turn <= endTurn; turn++) {
    life += log
      .filter((e) => e.to === pilotId && e.turn === turn)
      .reduce((sum, e) => sum + e.delta, 0);
    if (life <= 0) return turn;
  }
  return undefined;
}

export interface Streak {
  kind: 'win' | 'loss';
  length: number;
}

/** Spec 6.3 — deck on a 4-game run, pilot on a 3-game skid. */
export function streakForDeck(matches: readonly Match[], deckId: string): Streak | null {
  const played = matchesForDeck(matches, deckId);
  if (played.length === 0) return null;
  const won = (m: Match) => m.entries.find((e) => e.deckId === deckId)!.pilotId === m.winnerPilotId;
  const kind = won(played[0]!) ? 'win' : 'loss';
  let length = 0;
  for (const m of played) {
    if (won(m) === (kind === 'win')) length++;
    else break;
  }
  return { kind, length };
}
